import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Language, Project, AppPage } from './types';
import { translations } from './data/translations';
import { Navbar } from './components/Navbar';
import { MobileFloatingNav } from './components/MobileFloatingNav';
import { ExecutiveHeroBanner } from './components/ExecutiveHeroBanner';
import { Hero } from './components/Hero';
import { Footer } from './components/Footer';
import { PageHeaderBanner } from './components/PageHeaderBanner';
import { InstantAiFestivalBanner } from './components/InstantAiFestivalBanner';

// Lazy load non-critical multi-page views to optimize initial bundle size & FCP
const CampaignPyramidSection = lazy(() => import('./components/campaign/CampaignPyramidSection').then(m => ({ default: m.CampaignPyramidSection })));
const ServicesSection = lazy(() => import('./components/ServicesSection').then(m => ({ default: m.ServicesSection })));
const AlgoTradingSection = lazy(() => import('./components/AlgoTradingSection').then(m => ({ default: m.AlgoTradingSection })));
const PortfolioShowcase = lazy(() => import('./components/PortfolioShowcase').then(m => ({ default: m.PortfolioShowcase })));
const TechComparison = lazy(() => import('./components/TechComparison').then(m => ({ default: m.TechComparison })));
const ProjectCalculator = lazy(() => import('./components/ProjectCalculator').then(m => ({ default: m.ProjectCalculator })));
const WhyChooseUs = lazy(() => import('./components/WhyChooseUs').then(m => ({ default: m.WhyChooseUs })));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({ default: m.Testimonials })));
const FaqSection = lazy(() => import('./components/FaqSection').then(m => ({ default: m.FaqSection })));
const ContactSection = lazy(() => import('./components/ContactSection').then(m => ({ default: m.ContactSection })));
const SeoStrategyHub = lazy(() => import('./components/SeoStrategyHub').then(m => ({ default: m.SeoStrategyHub })));
import { 
  Sparkles, 
  Code2, 
  TrendingUp, 
  Briefcase, 
  Cpu, 
  Calculator, 
  ShieldCheck, 
  HelpCircle, 
  Mail, 
  BookOpen,
  ArrowLeft, 
  ArrowRight,
  CheckCircle2,
  Zap,
  MessageCircle,
  Phone
} from 'lucide-react';
import { WHATSAPP_PHONE_DISPLAY, createWhatsAppUrl } from './utils/whatsapp';
import { 
  getLangFromUrl, 
  getSavedManualLang, 
  getBrowserHeuristicLang, 
  detectGeoIpLanguage, 
  setSavedManualLang 
} from './utils/langDetector';

export default function App() {
  const [lang, setLang] = useState<Language>(() => {
    const urlLang = getLangFromUrl();
    if (urlLang) return urlLang;
    const manual = getSavedManualLang();
    if (manual) return manual;
    return getBrowserHeuristicLang();
  });
  const [currentPage, setCurrentPage] = useState<AppPage>('home');
  const [selectedTechForQuote, setSelectedTechForQuote] = useState<string>('custom');
  const [contactPlanDetails, setContactPlanDetails] = useState<{
    tech: string;
    scope: string;
    features: string[];
    costToman: string;
    costUsd: string;
    days: number;
  } | null>(null);

  const isRtl = lang === 'fa';
  const ArrowIcon = isRtl ? ArrowLeft : ArrowRight;

  // Asynchronous Geo-IP detection on mount (handles VPN / foreign IP smoothly)
  useEffect(() => {
    const urlLang = getLangFromUrl();
    const manual = getSavedManualLang();
    if (!urlLang && !manual) {
      detectGeoIpLanguage().then((detected) => {
        if (detected && detected !== lang) {
          setLang(detected);
        }
      });
    }
  }, []);

  const handleLanguageChange = (newLang: Language) => {
    setLang(newLang);
    setSavedManualLang(newLang);
  };

  // Sync document language, direction, and dynamic SEO titles
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'fa' ? 'rtl' : 'ltr';

    if (lang === 'fa') {
      document.title = 'سورنا آی‌تی (Sorena IT) | طراحی انواع وب‌سایت، ربات‌نویسی فارکس و هوش مصنوعی';
    } else {
      document.title = 'Sorena IT | Professional Web Design, Forex Trading Bots & AI Engineering';
    }

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      if (lang === 'fa') {
        metaDesc.setAttribute('content', 'طراحی انواع وبسایت های حرفه ایی از دم قسط. برنامه نویسی و ربات نویسی فارکس . فوق تخصص در هوش مصنوعی.');
      } else {
        metaDesc.setAttribute('content', 'Sorena IT: Professional bespoke web design with flexible installment plans, Forex algorithmic trading & MQL5 bots, and specialized artificial intelligence engineering.');
      }
    }
  }, [lang]);

  const handleNavigate = (page: AppPage) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenAlgoConsultation = (details?: { strategyType: string; platform: string }) => {
    if (details) {
      const scopeLabel = details.strategyType || (lang === 'fa' ? 'اکسپرت اتوماتیک فارکس' : 'Automated Forex Expert Advisor');
      const featuresList = lang === 'fa'
        ? ['کدنویسی سورس باز', 'بک‌تست کیفیت بالا', 'مدیریت ریسک پراپ فرم']
        : ['100% Open-Source Code', 'High-Quality Tick Backtest', 'Prop Firm Risk Management'];
      const costEstimate = lang === 'fa'
        ? 'بر اساس استراتژی (توافقی)'
        : 'Custom Strategy Quote';

      setContactPlanDetails({
        tech: details.platform || 'MQL5 / PineScript',
        scope: scopeLabel,
        features: featuresList,
        costToman: costEstimate,
        costUsd: 'Custom Quote',
        days: 7
      });
    }
    handleNavigate('contact');
  };

  const handleOpenCalculator = () => {
    handleNavigate('calculator');
  };

  const handleExplorePortfolio = () => {
    handleNavigate('portfolio');
  };

  const handleSelectStackForQuote = (stackId: string) => {
    setSelectedTechForQuote(stackId);
    handleNavigate('calculator');
  };

  const handleSelectProjectForQuote = (project: Project) => {
    setSelectedTechForQuote(project.category);
    handleNavigate('calculator');
  };

  const handleApplyPlanToContact = (planDetails: {
    tech: string;
    scope: string;
    features: string[];
    costToman: string;
    costUsd: string;
    days: number;
  }) => {
    setContactPlanDetails(planDetails);
    handleNavigate('contact');
  };

  const handleApplyCampaignToQuote = (couponData: {
    campaignName: string;
    discountedPriceToman: number;
    savedToman: number;
    referral1Url: string;
    referral2Url: string;
    trackingId?: string;
  }) => {
    setContactPlanDetails({
      tech: 'طراحی وب‌سایت (تخفیف کمپین هرمی ۳۰ میلیونی)',
      scope: `کمپین تخفیف ۱۰۰ به ۳۰ میلیون تومان (کد رهگیری: ${couponData.trackingId || 'SRN-CAMP-30M'})`,
      features: [
        `وب‌سایت مشتری معرفی ۱: ${couponData.referral1Url}`,
        `وب‌سایت مشتری معرفی ۲: ${couponData.referral2Url}`,
        'تخفیف استثنایی ۷۰ درصدی اعمال شد (۷۰ میلیون تومان صرفه‌جویی)'
      ],
      costToman: '۳۰,۰۰۰,۰۰۰ تومان (نهایی)',
      costUsd: '$750 (Special Campaign)',
      days: 10
    });
    handleNavigate('contact');
  };

  return (
    <div className="min-h-screen bg-[#0c0704] text-[#fcf9f2] selection:bg-amber-400 selection:text-stone-950 font-sans">
      {/* Desktop Sticky Top Navigation (Hidden on Mobile & Tablet) */}
      <Navbar
        lang={lang}
        onLanguageChange={handleLanguageChange}
        onOpenQuote={handleOpenCalculator}
        currentPage={currentPage}
        onNavigate={handleNavigate}
      />

      {/* Mobile & Tablet Floating Tubelight Navigation Bar */}
      <MobileFloatingNav
        lang={lang}
        onLanguageChange={handleLanguageChange}
        currentPage={currentPage}
        onNavigate={handleNavigate}
        onOpenQuote={handleOpenCalculator}
      />

      {/* Main Dynamic Multi-Page Router */}
      <main id="main-content" className="pb-20 lg:pb-0">
        {/* ============================================================ */}
        {/* PAGE 1: HOME (صفحه اصلی - Renders immediately without Suspense blocking) */}
        {/* ============================================================ */}
        {currentPage === 'home' && (
          <div className="space-y-0">
            {/* 100% Full-Width Executive Hero Banner */}
            <div id="executive-banner" className="pt-0 lg:pt-14 min-h-[580px] lg:min-h-[640px]">
              <ExecutiveHeroBanner
                lang={lang}
                onOpenCalculator={handleOpenCalculator}
                onExplorePortfolio={handleExplorePortfolio}
                onOpenContact={() => handleNavigate('contact')}
              />
            </div>

            {/* Main Interactive Architecture & Tech Hero */}
            <Hero
              lang={lang}
              onOpenCalculator={handleOpenCalculator}
              onExplorePortfolio={handleExplorePortfolio}
            />

            {/* High-Impact Campaign Teaser Banner on Home */}
            <section className="py-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#201007] via-[#2c170a] to-[#1c0e06] p-6 sm:p-10 border border-amber-500/50 shadow-2xl shadow-amber-950/70">
                <div className="absolute -top-24 -right-24 w-72 h-72 bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />
                <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6">
                  <div className="space-y-3 text-center lg:text-start">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-bold">
                      <Sparkles className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
                      <span>{lang === 'fa' ? 'پیشنهاد ویژه و محدود' : 'Exclusive Limited Offer'}</span>
                    </div>
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-white">
                      {lang === 'fa' 
                        ? 'کمپین تخفیف هرمی سورنا آی‌تی: طراحی سایت ۱۰۰ میلیونی فقط ۳۰ میلیون!'
                        : 'Pyramid Campaign: $1,000 USD Web Design for only $300 USD!'}
                    </h2>
                    <p className="text-xs sm:text-sm text-amber-100/90 max-w-2xl leading-relaxed">
                      {lang === 'fa'
                        ? 'با معرفی تنها ۲ مشتری و درج آدرس وب‌سایت آن‌ها، از ۷۰٪ تخفیف واقعی بهره‌مند شده و پروژه خود را با ۳۰ میلیون تومان و پشتیبانی کامل آغاز کنید.'
                        : 'Refer 2 client websites to unlock a genuine 70% discount on your full website for only $300 USD (reduced from $1,000) with included hosting and SLA support.'}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full lg:w-auto">
                    <button
                      onClick={() => handleNavigate('campaign')}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 hover:from-amber-400 hover:to-yellow-300 text-stone-950 font-black text-sm shadow-xl shadow-amber-500/25 transition-all cursor-pointer"
                    >
                      <span>{lang === 'fa' ? 'مشاهده شرایط کمپین و دریافت تخفیف' : 'View Campaign & Get Discount'}</span>
                      <ArrowIcon className="w-4 h-4 text-stone-950" />
                    </button>
                    <button
                      onClick={() => handleNavigate('calculator')}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[#241308]/90 hover:bg-[#341b0b] text-amber-200 border border-amber-700/60 font-semibold text-sm transition-all cursor-pointer"
                    >
                      <span>{lang === 'fa' ? 'محاسبه آنلاین قیمت' : 'Price Calculator'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Standalone Instant AI Web Design Festival Banner (طراحی فوری با هوش مصنوعی ۳M بجای ۱۰M) */}
            <section className="py-4 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
              <InstantAiFestivalBanner
                lang={lang}
                onOpenCalculator={handleOpenCalculator}
                onOpenContact={() => handleNavigate('contact')}
              />
            </section>

            {/* All-Pages Visual Navigation Hub */}
            <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-amber-900/30">
              <div className="text-center max-w-3xl mx-auto mb-10 space-y-2">
                <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">
                  {lang === 'fa' ? 'صفحات اختصاصی وب‌سایت سورنا آی‌تی' : 'Sorena IT Dedicated Portals'}
                </span>
                <h2 className="text-2xl sm:text-3xl font-black text-white">
                  {lang === 'fa' ? 'دسترسی سریع به تمام بخش‌ها و خدمات' : 'Quick Access to All Services & Sections'}
                </h2>
                <p className="text-xs sm:text-sm text-stone-300">
                  {lang === 'fa' 
                    ? 'هر یک از خدمات و امکانات به صورت یک صفحه مجزا، کامل و تعاملی پیاده‌سازی شده است'
                    : 'Each specialized service and feature is crafted into its own dedicated, interactive page'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {[
                  {
                    id: 'campaign' as AppPage,
                    title: lang === 'fa' ? 'کمپین تخفیف هرمی (۷۰٪)' : 'Pyramid Discount Campaign',
                    desc: lang === 'fa' ? 'تخفیف ۱۰۰ به ۳۰ میلیون تومان با معرفی ۲ مشتری' : 'From $1,000 to $300 USD with 2 client referrals',
                    icon: Sparkles,
                    badge: lang === 'fa' ? 'تخفیف ویژه' : 'Special Offer',
                    highlight: true
                  },
                  {
                    id: 'services' as AppPage,
                    title: lang === 'fa' ? 'خدمات تخصصی مهندسی وب' : 'Web Engineering Services',
                    desc: lang === 'fa' ? 'طراحی سایت مدرن، وردپرس پرسرعت و فروشگاه آنلاین' : 'Custom Web Apps, High-speed WordPress & E-Commerce',
                    icon: Code2,
                    badge: lang === 'fa' ? '۶ خدمت اصلی' : '6 Core Services'
                  },
                  {
                    id: 'algo-trading' as AppPage,
                    title: lang === 'fa' ? 'معاملات الگوریتمی و اکسپرت MQL5' : 'Forex Algo & MQL5 Engineering',
                    desc: lang === 'fa' ? 'کدنویسی استراتژی‌های فارکس، متاتریدر و تریدینگ‌ویو' : 'Automated Trading EAs, MetaTrader & Pine Script',
                    icon: TrendingUp,
                    badge: lang === 'fa' ? 'کدنویسی فارکس' : 'Forex Engineering'
                  },
                  {
                    id: 'portfolio' as AppPage,
                    title: lang === 'fa' ? 'نمونه‌کارهای موفق و پروژه‌ها' : 'Delivered Portfolio Projects',
                    desc: lang === 'fa' ? 'پروژه‌های تحویل‌شده با نمره سرعت ۱۰۰ و استانداردهای گوگل' : 'Deployed live projects with perfect Core Web Vitals',
                    icon: Briefcase,
                    badge: lang === 'fa' ? 'پروژه‌های اجرایی' : 'Live Projects'
                  },
                  {
                    id: 'calculator' as AppPage,
                    title: lang === 'fa' ? 'محاسبه‌گر آنلاین هزینه و زمان' : 'Interactive Price Calculator',
                    desc: lang === 'fa' ? 'استعلام آنی قیمت، امکانات اختصاصی و صدور پیش‌فاکتور' : 'Instant transparent timeline & cost calculation',
                    icon: Calculator,
                    badge: lang === 'fa' ? 'استعلام لحظه‌ای' : 'Instant Quote'
                  },
                  {
                    id: 'tech-comparison' as AppPage,
                    title: lang === 'fa' ? 'مقایسه تخصصی تکنولوژی‌ها' : 'Tech Stack Benchmark Matrix',
                    desc: lang === 'fa' ? 'تحلیل سرعت، امنیت و مقیاس‌پذیری کد در برابر وردپرس' : 'Speed, security & maintenance comparison matrix',
                    icon: Cpu,
                    badge: lang === 'fa' ? 'تحلیل فنی' : 'Tech Benchmark'
                  },
                  {
                    id: 'why-us' as AppPage,
                    title: lang === 'fa' ? 'چرا سورنا آی‌تی و نظرات مشتریان' : 'Why Sorena IT & Reviews',
                    desc: lang === 'fa' ? 'تضمین آپ‌تایم ۹۹.۹٪، پشتیبانی مستقیم و رضایت کارفرمایان' : '99.9% uptime, clean code & verified testimonials',
                    icon: ShieldCheck,
                    badge: lang === 'fa' ? 'تضمین کیفیت' : 'Quality Guaranteed'
                  },
                  {
                    id: 'faq' as AppPage,
                    title: lang === 'fa' ? 'سوالات متداول کارفرمایان' : 'Frequently Asked Questions',
                    desc: lang === 'fa' ? 'پاسخ شفاف به سوالات قرارداد، پرداخت، سورس‌کد و هاست' : 'Contracts, milestones, code delivery & hosting FAQ',
                    icon: HelpCircle,
                    badge: lang === 'fa' ? 'پاسخگویی سریع' : 'Fast Response'
                  },
                  {
                    id: 'blog' as AppPage,
                    title: lang === 'fa' ? 'وبلاگ، استراتژی سئو و مقالات' : 'SEO Strategy & Technical Guides',
                    desc: lang === 'fa' ? 'تحلیل شفاف قیمت‌های ۱۴۰۵، معماری وردپرس و اختصاصی، سئو تکنیکال و سئو محلی' : 'Transparent pricing analysis, custom vs WP, technical SEO & local search',
                    icon: BookOpen,
                    badge: lang === 'fa' ? 'دانشنامه و مقالات' : 'Guides & Insights',
                    highlight: true
                  },
                  {
                    id: 'contact' as AppPage,
                    title: lang === 'fa' ? 'تماس مستقیم و ثبت سفارش' : 'Contact & Submit Project',
                    desc: lang === 'fa' ? 'مشاوره تلفنی، واتس‌اپ و تلگرام با مهندس کاویانی‌پور' : 'Direct phone & WhatsApp call with Arash Kavianipoor',
                    icon: Mail,
                    badge: lang === 'fa' ? 'مشاوره رایگان' : 'Free Consultation'
                  },
                ].map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <div
                      key={item.id}
                      onClick={() => handleNavigate(item.id)}
                      className={`group p-5 rounded-2xl transition-all duration-200 cursor-pointer flex flex-col justify-between border ${
                        item.highlight
                          ? 'bg-gradient-to-b from-[#251307] via-[#1c0e06] to-[#120904] border-amber-500/60 hover:border-amber-400 shadow-lg shadow-amber-950/60 hover:-translate-y-1'
                          : 'bg-[#180e07]/90 hover:bg-[#221309] border-amber-900/40 hover:border-amber-600/60 shadow-md hover:-translate-y-1'
                      }`}
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="p-2.5 rounded-xl bg-[#28150a] border border-amber-800/60 text-amber-400 group-hover:text-yellow-300 transition-colors">
                            <ItemIcon className="w-5 h-5" />
                          </div>
                          <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-amber-950/70 text-amber-300 border border-amber-800/60">
                            {item.badge}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-white group-hover:text-amber-300 transition-colors">
                            {item.title}
                          </h3>
                          <p className="text-xs text-stone-300 mt-1 leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </div>

                      <div className="pt-4 mt-2 border-t border-amber-900/40 flex items-center justify-between text-xs font-semibold text-amber-400 group-hover:text-amber-200">
                        <span>{lang === 'fa' ? 'مشاهده صفحه اختصاصی' : 'View Dedicated Page'}</span>
                        <ArrowIcon className="w-3.5 h-3.5 transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Curated Previews on Home - Deferred with Isolated Suspense Boundaries to prevent CLS */}
            <Suspense fallback={
              <div className="min-h-[600px] flex items-center justify-center p-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-amber-500/30 border-t-amber-400 animate-spin" />
                </div>
              </div>
            }>
              <ServicesSection
                lang={lang}
                onSelectServiceForQuote={handleSelectStackForQuote}
              />
            </Suspense>

            <Suspense fallback={
              <div className="min-h-[600px] flex items-center justify-center p-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-amber-500/30 border-t-amber-400 animate-spin" />
                </div>
              </div>
            }>
              <PortfolioShowcase
                lang={lang}
                onSelectProjectForQuote={handleSelectProjectForQuote}
              />
            </Suspense>

            <Suspense fallback={
              <div className="min-h-[500px] flex items-center justify-center p-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-amber-500/30 border-t-amber-400 animate-spin" />
                </div>
              </div>
            }>
              <Testimonials lang={lang} />
            </Suspense>
          </div>
        )}

        {/* Dedicated Non-Home Subpages: Wrapped in Suspense Boundary */}
        {currentPage !== 'home' && (
          <Suspense fallback={
            <div className="min-h-[60vh] flex items-center justify-center p-12">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 rounded-full border-2 border-amber-500/30 border-t-amber-400 animate-spin" />
                <span className="text-xs text-stone-400">{lang === 'fa' ? 'در حال بارگذاری صفحه...' : 'Loading page...'}</span>
              </div>
            </div>
          }>

        {/* ============================================================ */}
        {/* PAGE 2: CAMPAIGN (صفحه اختصاصی کمپین تخفیف هرمی ۱۰۰ به ۳۰ میلیون) */}
        {/* ============================================================ */}
        {currentPage === 'campaign' && (
          <div className="space-y-8">
            <PageHeaderBanner
              lang={lang}
              currentPage={currentPage}
              onNavigate={handleNavigate}
              title={lang === 'fa' ? 'کمپین تخفیف هرمی (۱۰۰ به ۳۰ میلیون تومان)' : 'Pyramid Referral Campaign (70% OFF)'}
              subtitle={lang === 'fa' 
                ? 'با معرفی تنها ۲ مشتری و ثبت آدرس وب‌سایت آن‌ها، وب‌سایت ۱۰۰ میلیونی کامل و حرفه‌ای خود را با ۷۰٪ تخفیف استثنایی و تنها با ۳۰ میلیون تومان دریافت کنید.'
                : 'Refer 2 clients with their active website URLs and get your 100M Toman web project for only 30M Toman with full guarantees.'}
              badge={lang === 'fa' ? '۷۰٪ تخفیف هرمی واقعی' : '70% Verified Referral Discount'}
              icon={Sparkles}
            />

            <div className="pb-16">
              <CampaignPyramidSection
                lang={lang}
                onApplyCampaignToQuote={handleApplyCampaignToQuote}
              />
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* PAGE 3: SERVICES (صفحه اختصاصی خدمات تخصصی طراحی وب) */}
        {/* ============================================================ */}
        {currentPage === 'services' && (
          <div className="space-y-8">
            <PageHeaderBanner
              lang={lang}
              currentPage={currentPage}
              onNavigate={handleNavigate}
              title={lang === 'fa' ? 'خدمات تخصصی طراحی و توسعه وب‌سایت' : 'Web Engineering & Development Services'}
              subtitle={lang === 'fa'
                ? 'کدنویسی اختصاصی مدرن با React 19 و Next.js، وردپرس بهینه‌سازی‌شده، فروشگاه‌های اینترنتی پرسرعت و سئوی تکنیکال با بالاترین نمرات Core Web Vitals.'
                : 'Bespoke web applications, high-performance WordPress themes, scalable e-commerce systems, and advanced technical SEO.'}
              badge={lang === 'fa' ? 'راهکارهای مهندسی نرم‌افزار' : 'Engineering Solutions'}
              icon={Code2}
            />

            <div className="pb-16 space-y-8">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <InstantAiFestivalBanner
                  lang={lang}
                  onOpenCalculator={handleOpenCalculator}
                  onOpenContact={() => handleNavigate('contact')}
                />
              </div>

              <ServicesSection
                lang={lang}
                onSelectServiceForQuote={handleSelectStackForQuote}
              />

              {/* Bottom Services CTA Card */}
              <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8">
                <div className="p-6 sm:p-8 rounded-2xl bg-[#1c0e06] border border-amber-800/60 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl shadow-amber-950/40">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">
                      {lang === 'fa' ? 'نیاز به استعلام هزینه دقیق و اختصاصی دارید؟' : 'Need a transparent and exact price calculation?'}
                    </h3>
                    <p className="text-xs sm:text-sm text-amber-100/80">
                      {lang === 'fa' 
                        ? 'وارد محاسبه‌گر آنلاین شده و فاکتور تفکیک‌شده امکانات را در کمتر از ۱ دقیقه دریافت نمایید.'
                        : 'Use our interactive calculator to customize every feature and receive an instant breakdown.'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleNavigate('calculator')}
                    className="shrink-0 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 hover:from-amber-400 hover:to-yellow-300 text-stone-950 font-black text-xs shadow-lg shadow-amber-500/25 transition-all cursor-pointer"
                  >
                    {lang === 'fa' ? 'ورود به محاسبه‌گر قیمت →' : 'Open Price Calculator →'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* PAGE 4: ALGO-TRADING (صفحه اختصاصی کدنویسی فارکس و متاتریدر MQL5) */}
        {/* ============================================================ */}
        {currentPage === 'algo-trading' && (
          <div className="space-y-8">
            <PageHeaderBanner
              lang={lang}
              currentPage={currentPage}
              onNavigate={handleNavigate}
              title={lang === 'fa' ? 'معاملات الگوریتمی و کدنویسی استراتژی‌های فارکس (MQL5 / Pine Script)' : 'Forex Algorithmic Trading & MQL5 / Pine Script Development'}
              subtitle={lang === 'fa'
                ? 'طراحی و ساخت اکسپرت‌های متاتریدر ۴ و ۵، اسکریپت‌های تحلیلی تریدینگ‌ویو، وب‌هوک اختصاصی و سیستم‌های رعایت سخت‌گیرانه قوانین پراپ‌فرم.'
                : 'Custom MetaTrader 4/5 automated Expert Advisors, TradingView Pine Script webhooks, prop firm risk rule engines, and tick-accurate backtesting.'}
              badge={lang === 'fa' ? 'سیستم‌های معاملات خودکار' : 'Automated Trading Engines'}
              icon={TrendingUp}
            />

            <div className="pb-16">
              <AlgoTradingSection
                lang={lang}
                onOpenConsultation={handleOpenAlgoConsultation}
              />
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* PAGE 5: PORTFOLIO (صفحه اختصاصی نمونه‌کارهای موفق و پروژه‌ها) */}
        {/* ============================================================ */}
        {currentPage === 'portfolio' && (
          <div className="space-y-8">
            <PageHeaderBanner
              lang={lang}
              currentPage={currentPage}
              onNavigate={handleNavigate}
              title={lang === 'fa' ? 'نمونه‌کارهای اجرایی و پروژه‌های تحویل‌شده' : 'Featured Projects & Delivered Case Studies'}
              subtitle={lang === 'fa'
                ? 'مجموعه وب‌سایت‌های سازمانی، فروشگاهی و پلتفرم‌های اختصاصی پیاده‌سازی شده با سرعت زیر ۱ ثانیه، معماری تمیز و امتیاز ۱۰۰ در گوگل پیج‌اسپید.'
                : 'Explore delivered corporate websites, e-commerce applications, and high-performance custom portals with verified speed audits.'}
              badge={lang === 'fa' ? 'پروژه‌های موفق کارفرمایان' : 'Client Case Studies'}
              icon={Briefcase}
            />

            <div className="pb-16">
              <PortfolioShowcase
                lang={lang}
                onSelectProjectForQuote={handleSelectProjectForQuote}
              />
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* PAGE 6: TECH-COMPARISON (صفحه اختصاصی مقایسه تکنولوژی‌ها) */}
        {/* ============================================================ */}
        {currentPage === 'tech-comparison' && (
          <div className="space-y-8">
            <PageHeaderBanner
              lang={lang}
              currentPage={currentPage}
              onNavigate={handleNavigate}
              title={lang === 'fa' ? 'مقایسه جامع فریم‌ورک‌ها و سیستم‌های مدیریت محتوا' : 'Tech Stack Benchmark Matrix: Custom Code vs CMS'}
              subtitle={lang === 'fa'
                ? 'بررسی موشکافانه تفاوت‌های فنی، سرعت، امنیت، سئو، هزینه نگهداری و مقیاس‌پذیری کدنویسی اختصاصی در مقایسه با وردپرس، تایپو۳ و جوملا.'
                : 'In-depth benchmark comparison of loading speed, security vulnerabilities, SEO capabilities, and scalability between bespoke code and CMS.'}
              badge={lang === 'fa' ? 'ماتریس تحلیل فنی' : 'Technical Analysis Matrix'}
              icon={Cpu}
            />

            <div className="pb-16">
              <TechComparison
                lang={lang}
                onSelectStackForQuote={handleSelectStackForQuote}
              />
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* PAGE 7: CALCULATOR (صفحه اختصاصی محاسبه‌گر آنلاین قیمت و زمان) */}
        {/* ============================================================ */}
        {currentPage === 'calculator' && (
          <div className="space-y-8">
            <PageHeaderBanner
              lang={lang}
              currentPage={currentPage}
              onNavigate={handleNavigate}
              title={lang === 'fa' ? 'محاسبه‌گر آنلاین هزینه و زمان‌بندی تحویل پروژه' : 'Interactive Project Cost & Timeline Calculator'}
              subtitle={lang === 'fa'
                ? 'پلتفرم مورد نظر، تعداد صفحات، ماژول‌های فنی و بسته‌های سئو را انتخاب نموده و پیش‌فاکتور شفاف به همراه زمان‌بندی دقیق تحویل را در لحظه دریافت فرمایید.'
                : 'Select your preferred stack, estimated pages, and custom feature modules to compute a transparent budget estimate and delivery timeline.'}
              badge={lang === 'fa' ? 'پیش‌فاکتور شفاف و لحظه‌ای' : 'Instant Transparent Quote'}
              icon={Calculator}
            />

            <div className="pb-16">
              <ProjectCalculator
                lang={lang}
                preselectedTech={selectedTechForQuote}
                onApplyPlanToContact={handleApplyPlanToContact}
              />
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* PAGE 8: WHY-US & TESTIMONIALS (صفحه اختصاصی چرا سورنا آی‌تی و نظرات) */}
        {/* ============================================================ */}
        {currentPage === 'why-us' && (
          <div className="space-y-8">
            <PageHeaderBanner
              lang={lang}
              currentPage={currentPage}
              onNavigate={handleNavigate}
              title={lang === 'fa' ? 'چرا سورنا آی‌تی و نظرات کارفرمایان' : 'Why Choose Sorena IT & Client Testimonials'}
              subtitle={lang === 'fa'
                ? 'تعهد به کدهای استاندارد و بهینه‌سازی‌شده، گارانتی بازگشت وجه، آپ‌تایم ۹۹.۹٪، پشتیبانی مستقیم توسط مهندس کاویانی‌پور و رضایت تضمین‌شده کارفرمایان.'
                : 'Strict coding discipline, 99.9% uptime SLA, money-back satisfaction guarantees, and direct engineering guidance by Arash Kavianipoor.'}
              badge={lang === 'fa' ? 'تضمین کیفیت و پشتیبانی' : 'Quality SLA & Trust'}
              icon={ShieldCheck}
            />

            <div className="pb-16 space-y-12">
              <WhyChooseUs lang={lang} />
              <Testimonials lang={lang} />
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* PAGE 9: FAQ (صفحه اختصاصی سوالات متداول کارفرمایان) */}
        {/* ============================================================ */}
        {currentPage === 'faq' && (
          <div className="space-y-8">
            <PageHeaderBanner
              lang={lang}
              currentPage={currentPage}
              onNavigate={handleNavigate}
              title={lang === 'fa' ? 'سوالات متداول مشتریان و کارفرمایان (FAQ)' : 'Frequently Asked Questions (FAQ)'}
              subtitle={lang === 'fa'
                ? 'پاسخ کامل و شفاف به مهم‌ترین پرسش‌ها در خصوص نحوه عقد قرارداد، پرداخت اقساطی، مالکیت ۱۰۰٪ سورس‌کد، هاست و پشتیبانی فنی.'
                : 'Clear answers concerning project contract milestones, source code ownership, payment schedules, hosting setup, and post-launch maintenance.'}
              badge={lang === 'fa' ? 'مرکز پاسخگویی شفاف' : 'Answers & Clarity'}
              icon={HelpCircle}
            />

            <div className="pb-16 space-y-8">
              <FaqSection lang={lang} />

              {/* Direct Question Banner */}
              <div className="max-w-4xl mx-auto px-4 sm:px-6">
                <div className="p-6 rounded-2xl bg-[#1c0e06] border border-amber-800/60 text-center space-y-4 shadow-xl shadow-amber-950/40">
                  <h3 className="text-base sm:text-lg font-bold text-white">
                    {lang === 'fa' ? 'پاسخ سوال خود را در این بخش پیدا نکردید؟' : 'Did not find the exact answer to your inquiry?'}
                  </h3>
                  <p className="text-xs sm:text-sm text-amber-100/80 max-w-xl mx-auto">
                    {lang === 'fa'
                      ? 'همین حالا سوال خود را مستقیماً در واتس‌اپ برای مهندس کاویانی‌پور ارسال کنید تا در اسرع وقت پاسخ کامل دریافت نمایید.'
                      : 'Send your question directly on WhatsApp to Arash Kavianipoor for immediate clarification.'}
                  </p>
                  <a
                    href={createWhatsAppUrl(lang === 'fa' ? 'سلام جناب مهندس کاویانی‌پور، در رابطه با خدمات طراحی سایت سوالی داشتم:' : 'Hello Mr. Kavianipoor, I have a specific question:')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 hover:from-amber-400 hover:to-yellow-300 text-stone-950 font-black text-xs shadow-lg shadow-amber-500/25 transition-all cursor-pointer"
                  >
                    <MessageCircle className="w-4 h-4 text-stone-950" />
                    <span>{lang === 'fa' ? 'ارسال سوال در واتس‌اپ:' : 'Ask on WhatsApp:'}</span>
                    <span dir="ltr" className="font-mono font-bold">(+98)9128634908</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* PAGE 10: BLOG & SEO STRATEGY HUB (وبلاگ، استراتژی سئو و مقالات تخصصی) */}
        {/* ============================================================ */}
        {currentPage === 'blog' && (
          <div className="space-y-8">
            <PageHeaderBanner
              lang={lang}
              currentPage={currentPage}
              onNavigate={handleNavigate}
              title={lang === 'fa' ? 'دانشنامه و مقالات تخصصی سئو و طراحی وب' : 'SEO Strategy Hub & Technical Articles'}
              subtitle={lang === 'fa'
                ? 'راهنماهای جامع و کاملاً انسانی پیرامون تعرفه‌ها و قیمت‌های ۱۴۰۵، معماری وردپرس در برابر اختصاصی، سئو تکنیکال، Core Web Vitals و سئو محلی کرج و تهران.'
                : 'Human-crafted technical guides on 1405 web pricing, custom architecture vs WordPress, technical SEO, Core Web Vitals, and localized Iranian search strategy.'}
              badge={lang === 'fa' ? 'دانشنامه تخصصی و استراتژی محتوا' : 'Topical Authority Hub'}
              icon={BookOpen}
            />

            <div className="pb-16">
              <SeoStrategyHub
                lang={lang}
                onNavigate={handleNavigate}
              />
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* PAGE 11: CONTACT (صفحه اختصاصی تماس با ما و ثبت سفارش) */}
        {/* ============================================================ */}
        {currentPage === 'contact' && (
          <div className="space-y-8">
            <PageHeaderBanner
              lang={lang}
              currentPage={currentPage}
              onNavigate={handleNavigate}
              title={lang === 'fa' ? 'تماس با ما، مشاوره مستقیم و ثبت سفارش' : 'Direct Contact & Project Inquiries'}
              subtitle={lang === 'fa'
                ? 'ارتباط مستقیم تلفنی، واتس‌اپ و تلگرام با مهندس آرش کاویانی‌پور جهت مشاوره تخصصی رایگان، استعلام فنی و تعیین زمان‌بندی شروع پروژه.'
                : 'Direct line to Arash Kavianipoor via phone, WhatsApp, and Telegram for complimentary technical consultations and contract setup.'}
              badge={lang === 'fa' ? 'ارتباط مستقیم با مدیر فنی' : 'Direct Engineering Line'}
              icon={Mail}
            />

            <div className="pb-16">
              <ContactSection
                lang={lang}
                initialPlanDetails={contactPlanDetails}
              />
            </div>
          </div>
        )}

          </Suspense>
        )}
      </main>

      {/* Global Modern Footer with Smooth Page Navigation */}
      <Footer 
        lang={lang} 
        onNavigate={handleNavigate}
      />
    </div>
  );
}
